import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeviceLive } from '../../realtime/hooks';
import { PresenceBadge, SignalBars } from '../../ui/components';
import { formatMetric } from '../../ui/format';
import { signalLevel } from '../../ui/signal';

/** Each cell subscribes to ONE device and ONE field; a change elsewhere never re-renders it. */
export const PresenceCell = memo(function PresenceCell({ id }: { id: string }) {
  const presence = useDeviceLive(id, (d) => d?.presence);
  return <PresenceBadge presence={presence} />;
});

export const MetricCell = memo(function MetricCell({ id, metric }: { id: string; metric: string }) {
  const value = useDeviceLive(id, (d) => d?.metrics?.[metric]);
  return <span className="num">{formatMetric(metric, value)}</span>;
});

/** Signal bars of one device; selects a primitive key so only a change of bars/value re-renders it. */
export const SignalCell = memo(function SignalCell({
  id,
  hideEmpty = false,
}: {
  id: string;
  hideEmpty?: boolean;
}) {
  const { t } = useTranslation();
  const key = useDeviceLive(id, (d) => {
    const s = signalLevel(d?.metrics);
    return s ? `${s.bars}|${s.source}|${s.value}` : '';
  });
  const online = useDeviceLive(id, (d) => d?.presence === 'online');
  if (!key) return hideEmpty ? null : <span className="muted">—</span>;
  const [bars, source, value] = key.split('|') as [string, string, string];
  const n = Number(bars);
  const shown = formatMetric(source, Number(value));
  return (
    <SignalBars
      bars={n}
      stale={!online}
      title={
        n ? t('devices.signal.level', { bars: n, value: shown }) : t('devices.signal.none', { value: shown })
      }
    />
  );
});
